import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@givebutter/primitives/alert-dialog';
import { useToast } from '@givebutter/primitives/toast';
import { faExclamationTriangle } from '@fortawesome/pro-regular-svg-icons';
import { disassociateContact } from 'api/contacts/companies';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useCurrentContact } from 'customHooks/useCurrentContact';
import { accountQueryKeys } from 'queries/account-query-keys';
import { GivebutterAxiosError } from 'types/error';

interface DisassociateContactDialogProps extends AlertDialog.AlertDialogRootProps {
    contactId: number;
}

const DisassociateContactDialog = ({
    contactId,
    children,
    ...props
}: DisassociateContactDialogProps) => {
    const { error, success } = useToast();
    const { data: company } = useCurrentContact();
    const { accountId } = useCurrentAccount();
    const queryClient = useQueryClient();

    const { mutate: disassociate, isPending } = useMutation({
        mutationFn: (contactId: number) => {
            return disassociateContact(accountId, company.id, {
                contact_id: contactId,
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: accountQueryKeys.companyContacts(accountId, company.id),
                type: 'all',
            });
            success('Contact removed successfully.');
        },
        onError: (e: GivebutterAxiosError) => {
            error(e.response?.data.message ?? 'Failed to remove contact.');
        },
    });

    return (
        <AlertDialog.Root type="warning" {...props}>
            {children}
            <AlertDialog.Portal>
                <AlertDialog.Overlay />
                <AlertDialog.Content>
                    <AlertDialog.Header>
                        <AlertDialog.Icon customIcon={faExclamationTriangle} />
                        <AlertDialog.Close />
                    </AlertDialog.Header>
                    <AlertDialog.Body>
                        <AlertDialog.Title>
                            Remove contact from {company?.company_name}?
                        </AlertDialog.Title>
                        <AlertDialog.Description>
                            This does not delete the contact.
                        </AlertDialog.Description>
                    </AlertDialog.Body>
                    <AlertDialog.Footer>
                        <AlertDialog.Cancel>Close</AlertDialog.Cancel>
                        <AlertDialog.Action
                            onClick={() => {
                                disassociate(contactId);
                            }}
                            disabled={isPending}
                        >
                            Remove
                        </AlertDialog.Action>
                    </AlertDialog.Footer>
                </AlertDialog.Content>
            </AlertDialog.Portal>
        </AlertDialog.Root>
    );
};

const DisassociateContactDialogTrigger = AlertDialog.Trigger;

const Root = DisassociateContactDialog;
const Trigger = DisassociateContactDialogTrigger;

export {
    DisassociateContactDialog,
    DisassociateContactDialogTrigger,
    //
    Root,
    Trigger,
};
